import { AxiosError } from 'axios';
import type {
  ClaimResult,
  CohortEmpathy,
  GroupRequest,
  PostCallInfo,
  PostGroupRequestInput,
  PostGroupRequestResult,
  RequestResult,
} from '../types/empathy-requests';
import { deviceTimeZone } from '../utils/listening';
import api, { isGloballyHandled } from './api';

/**
 * Group empathy requests (P7). Each call returns the server's refusal as
 * {error, message} — the message is the server's own sentence, shown as is.
 * The agreement / norms 403s are ALSO raised globally by the API client (the
 * park-ask-replay sheets); when the user declines one, the refusal still
 * arrives here and is shown. A structured 402 / 403 for credits or a plan is
 * answered by the client's upgrade prompt alone: it comes back marked
 * `globallyHandled`, and the caller shows nothing of its own (#1058 follow-up).
 */

const FALLBACK = "Couldn't reach the server. Check your connection and try again.";

/**
 * The refusal the API client already answered, with the payload's own code
 * (the RFC 7807 body carries `error_code`, not `error`, so the readers below
 * took it for a network failure). One definition for both P7 services.
 */
export function globallyHandledRefusal<T>(error: unknown): RequestResult<T> | null {
  if (!isGloballyHandled(error)) return null;
  const data = error.response?.data as { error_code?: unknown; message?: unknown } | undefined;
  return {
    success: false,
    error: typeof data?.error_code === 'string' ? data.error_code : 'globally_handled',
    message: typeof data?.message === 'string' && data.message ? data.message : FALLBACK,
    globallyHandled: true,
  };
}

function refusal<T>(error: unknown): RequestResult<T> {
  const handled = globallyHandledRefusal<T>(error);
  if (handled) return handled;
  if (error instanceof AxiosError && error.response?.data) {
    const data = error.response.data as { error?: unknown; code?: unknown; message?: unknown };
    const code =
      typeof data.error === 'string'
        ? data.error
        : typeof data.code === 'string'
          ? data.code
          : null;
    if (code !== null) {
      return {
        success: false,
        error: code,
        message: typeof data.message === 'string' && data.message ? data.message : FALLBACK,
      };
    }
  }
  return { success: false, error: 'network', message: FALLBACK };
}

export async function getCohortEmpathy(cohortId: number): Promise<RequestResult<CohortEmpathy>> {
  try {
    const tz = deviceTimeZone();
    const response = await api.get<{ data: CohortEmpathy }>(
      `/cohorts/${cohortId}/empathy`,
      tz ? { params: { tz } } : undefined,
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

export async function postGroupRequest(
  input: PostGroupRequestInput,
): Promise<RequestResult<PostGroupRequestResult>> {
  try {
    const response = await api.post<{ data: PostGroupRequestResult }>('/empathy/requests', input);
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

export async function claimGroupRequest(id: number): Promise<RequestResult<ClaimResult>> {
  try {
    const response = await api.post<{ data: ClaimResult }>(`/empathy/requests/${id}/claim`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

export async function withdrawGroupRequest(
  id: number,
): Promise<RequestResult<{ request: GroupRequest }>> {
  try {
    const response = await api.post<{ data: { request: GroupRequest } }>(
      `/empathy/requests/${id}/withdraw`,
    );
    return { success: true, data: response.data.data };
  } catch (error) {
    return refusal(error);
  }
}

/** The post-call additions for a finished 1:1 session (B2, B6a). */
export async function getPostCall(
  sessionId: string | number,
): Promise<RequestResult<PostCallInfo | null>> {
  try {
    const response = await api.get<{ post_call?: PostCallInfo | null }>(
      `/feedback/session/${sessionId}`,
    );
    return { success: true, data: response.data.post_call ?? null };
  } catch (error) {
    return refusal(error);
  }
}
